import { FC, FormEvent } from 'react';
import { graphql, useMutation } from 'react-relay';

import PrimaryInterestSelect from './PrimaryInterestSelect';
import PrivateAdvisorAssistanceTextArea from './PrivateAdvisorAssistanceTextArea';
import PriceRangeSelect from './PriceRangeSelect';
import SmsWhatsAppFieldSet from './SmsWhatsAppFieldSet';
import SubmitButton from './SubmitButton';

import { iFramedPromoPartnershipPCFormValidator } from './helpers/iFramedPromoPartnershipPCFormValidator';

import {
    SET_FORM_DATA,
    SET_ERROR_MESSAGES,
    SET_SERVICE_ERROR,
    SET_STEP,
    SET_LOADING,
    STEP_FORM_SUBMITTED,
    DispatchType,
    ReducerState,
    FormData,
    FormField,
} from '../reducer';

import {
    IFramedPromoPartnershipPCFormMutation,
    SubmitPromoPartnershipPrivateClientRecordInput,
} from './__generated__/IFramedPromoPartnershipPCFormMutation.graphql';

import styles from './styles/IFramedPromoPartnershipPCForm.scss';

type Props = {
    state: ReducerState;
    dispatch: DispatchType;
};

const IFramedPromoPartnershipPCForm: FC<Props> = ({ state, dispatch }) => {
    const [commitSubmit] = useMutation<IFramedPromoPartnershipPCFormMutation>(
        graphql`
            mutation IFramedPromoPartnershipPCFormMutation(
                $input: SubmitPromoPartnershipPrivateClientRecordInput!
            ) {
                submitPromoPartnershipPrivateClientRecord(input: $input) {
                    recordId
                }
            }
        `
    );

    const { formData, errorMessages, isLoading, hasServiceError } = state;

    const {
        userId,
        primaryInterest,
        privateAdvisorAssistance,
        priceRange,
        receiveSmsUpdates,
        smsPhoneNumber,
        receiveWhatsAppUpdates,
        whatsAppPhoneNumber,
    } = formData;

    const {
        primaryInterest: primaryInterestErrorMessage,
        privateAdvisorAssistance: privateAdvisorAssistanceErrorMessage,
        priceRange: priceRangeErrorMessage,
        smsPhoneNumber: smsPhoneNumberErrorMessage,
        whatsAppPhoneNumber: whatsAppPhoneNumberErrorMessage,
    } = errorMessages;

    const validateField = (field: FormField, value: FormData[FormField]): void => {
        const { errors } = iFramedPromoPartnershipPCFormValidator({ [field]: value });
        const error = errors?.[field];
        dispatch({ type: SET_ERROR_MESSAGES, errorMessages: { [field]: error } });
    };

    const setField = (field: FormField, value: FormData[FormField]): void => {
        dispatch({ type: SET_FORM_DATA, formData: { [field]: value } });
        validateField(field, value);
    };

    const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        dispatch({ type: SET_SERVICE_ERROR, hasServiceError: false });
        const { errors, isValid } = iFramedPromoPartnershipPCFormValidator(formData);
        dispatch({ type: SET_ERROR_MESSAGES, errorMessages: errors });

        if (!isValid) {
            return;
        }

        dispatch({ type: SET_LOADING, isLoading: true });

        const input: SubmitPromoPartnershipPrivateClientRecordInput = {
            userId,
            source: window.location.href,
            primaryInterest,
            privateAdvisorAssistance,
            priceRange,
            receiveSmsUpdates,
            smsPhoneNumber,
            receiveWhatsAppUpdates,
            whatsAppPhoneNumber,
        };

        commitSubmit({
            variables: { input },
            onCompleted(response) {
                dispatch({ type: SET_LOADING, isLoading: false });
                if (response?.submitPromoPartnershipPrivateClientRecord?.recordId) {
                    dispatch({ type: SET_STEP, step: STEP_FORM_SUBMITTED });
                } else {
                    dispatch({ type: SET_SERVICE_ERROR, hasServiceError: true });
                }
            },
            onError() {
                dispatch({ type: SET_LOADING, isLoading: false });
                dispatch({ type: SET_SERVICE_ERROR, hasServiceError: true });
            },
        });
    };

    return (
        <form onSubmit={onSubmit} className={styles.form}>
            <PrimaryInterestSelect
                value={primaryInterest}
                setField={setField}
                errorMessage={primaryInterestErrorMessage}
                disabled={isLoading}
            />
            <PrivateAdvisorAssistanceTextArea
                value={privateAdvisorAssistance}
                setField={setField}
                errorMessage={privateAdvisorAssistanceErrorMessage}
                disabled={isLoading}
            />
            <PriceRangeSelect
                value={priceRange}
                setField={setField}
                errorMessage={priceRangeErrorMessage}
                disabled={isLoading}
            />
            <SmsWhatsAppFieldSet
                type="sms"
                checkboxValue={receiveSmsUpdates}
                inputValue={smsPhoneNumber}
                setField={setField}
                errorMessage={smsPhoneNumberErrorMessage}
                disabled={isLoading}
            />
            <SmsWhatsAppFieldSet
                type="whatsApp"
                checkboxValue={receiveWhatsAppUpdates}
                inputValue={whatsAppPhoneNumber}
                setField={setField}
                errorMessage={whatsAppPhoneNumberErrorMessage}
                disabled={isLoading}
            />
            <SubmitButton isLoading={isLoading} showError={hasServiceError} />
        </form>
    );
};

export default IFramedPromoPartnershipPCForm;
